import { gql } from "@apollo/client";
import { contentTypeSchemaApi, graphqlInstance } from "@platformx/authoring-apis";
import { convertToLowerCase, frameDocumentType, getSelectedBrand } from "@platformx/utilities";

const appendSelectedBrandForSchemaField = (contentInstance) => {
  const input = { ...contentInstance };
  if (Object.hasOwn(input, "brand_name_x_str")) {
    input.brand_name_x_str = `${getSelectedBrand() || ""}`;
  }
  return input;
};

export const findMaxCharLength = (validations) => {
  let length = 1000;
  if (validations.length > 0) {
    const findData = validations.find((x) => x.type === "maxLength");
    if (findData) {
      length = findData.value;
    }
  }
  return length;
};

export const handleLength = (valueData, validations, setRestOfChar, restOfChar) => {
  const maxCharLength = findMaxCharLength(validations);
  if (maxCharLength) {
    const lengthOfChar = convertToLowerCase(valueData).length;
    const rest = valueData ? maxCharLength - lengthOfChar : 0;
    setRestOfChar({
      ...restOfChar,
      restOfLength: rest < 0 ? 0 : rest,
      lengthOfState: lengthOfChar,
      reachLimit: maxCharLength <= lengthOfChar ? true : false,
    });
  }
};

const getValidationValue = (obj) => {
  if (obj.type === "pattern") {
    return new RegExp(obj.value);
  } else if (obj.type === "required") {
    return obj.value === false ? false : true;
  } else {
    return obj.value;
  }
};

export const fetchValidations = (validations) => {
  if (validations?.length > 0) {
    return validations.reduce((acc, obj) => {
      return {
        ...acc,
        [obj.type]: {
          value: getValidationValue(obj),
          message: obj.message,
        },
      };
    }, {});
  }
  return {};
};

export const calculateInitialValuesWithData = (initialData, data) => {
  const initialValues: any = {};
  const dataObj = Object.keys(initialData);
  dataObj.forEach((field) => {
    initialValues[field] = data[field];
  });
  return initialValues;
};

export const calculateInitialValues = (fields) => {
  const initialValues: any = {};
  if (fields?.length > 0) {
    fields.forEach((field) => {
      let defaultValue;
      if (field?.type === "boolean") {
        defaultValue = false;
      } else if (field?.type === "repeater") {
        defaultValue = [];
      } else if (field?.type === "button") {
        defaultValue = {};
      } else {
        defaultValue = "";
      }
      initialValues[field.name] = field?.value ?? field?.default_value ?? defaultValue;
    });
  }
  return initialValues;
};

export const createContentType = (contentInstance, contentType, schema_document_type) => {
  const newContent = {
    ...appendSelectedBrandForSchemaField(contentInstance),
    name: contentInstance?.title,
  };
  return contentTypeSchemaApi.saveContent({
    contentType: contentType,
    input: newContent,
    schema_document_type: schema_document_type || "contentType",
  });
};

export const updateContentType = (
  savedData,
  contentInstance,
  contentType,
  schema_document_type,
) => {
  const { name } = savedData.current;
  const newContent = {
    ...appendSelectedBrandForSchemaField(contentInstance),
    name: name,
  };
  return contentTypeSchemaApi.updateContent({
    contentType: contentType,
    input: newContent,
    schema_document_type: schema_document_type || "contentType",
  });
};

export const publishContentType = (savedData, contentType, schema_document_type) => {
  const { id, name } = savedData.current;
  const requestToSend = {
    document_name: name,
    document_id: id,
    status: "publish",
  };
  return contentTypeSchemaApi.publishContentType({
    contentType: contentType,
    input: requestToSend,
    schema_document_type: schema_document_type || "contentType",
  });
};

export const fetchSchema = (contentType) => {
  return contentTypeSchemaApi.getSchemaDynamic({
    filter: contentType,
  });
};

export const fetchSchemaWithData = (contentType, documentId, schema_document_type) => {
  return contentTypeSchemaApi.getSchemaDynamicWithData({
    contentType: contentType,
    name: documentId,
    schema_document_type: frameDocumentType(schema_document_type),
  });
};

export const dateAndTimeValidator = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (start <= end && end >= start) {
    return true;
  } else {
    return false;
  }
};

export const dynamicAPICall = async (query, responseVariable) => {
  try {
    // Ensure the query is a valid DocumentNode
    const documentNode =
      typeof query === "string"
        ? gql`
            ${query}
          `
        : query;
    const { data = {} } = await graphqlInstance.query({
      query: documentNode,
      variables: {},
      fetchPolicy: "no-cache",
    });
    return data[responseVariable] || [];
  } catch (err: any) {
    // ShowToastError(err || "Failed to load dropdown values");
    // eslint-disable-next-line no-console
    console.log("dynamicAPICallError", err);
    return [];
  }
};

export const getYearOptions = (startYear = 1970) => {
  const currentYear = new Date().getFullYear();

  return Array.from({ length: currentYear - startYear + 1 }, (_, index) => {
    const year = currentYear - index;

    return year.toString();
  });
};
